import { Box, Typography, Button, Stack } from "@mui/material";
import { AlertBanner } from "@qubit/autoparts";

import {
  useEffect,
  MutableRefObject,
  useCallback,
  useRef,
  useMemo
} from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";

import {
  addEvent,
  selectBinToPortTestSession,
  setSingleStepTestStatus,
  updateBinToPortLoopStatus
} from "~/features/testingTools/testingTools.slice";
import { ErrorItem } from "~/features/testingTools/testingTools.types";
import { findFirstPriorityError } from "~/features/testingTools/testingToolsUtils";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import usePrevious from "~/hooks/usePrevProps";
import { runAsyncSafely } from "~/lib/helpers";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useCloseBinMutation,
  useRequestNextBinMutation
} from "~/redux/warehouse/autostoreGrid.hooks";

type PortInheritedProps = {
  portId: number;
  portSideIndex: number;
  portIdToDisplay: number;
  binToPortTestLoopingEnabled: boolean;
  portReadyDispatchedRef: MutableRefObject<boolean>;
  updateBinToPortLoopBtnStatus: (statusValue: boolean) => void;
};

export const getAlertMessageFromErrorList = (
  errorItems: ErrorItem[],
  retryActionButtonText?: string,
  defaultRetryFn?: () => Promise<void> | void
) => {
  const errorToDisplay = findFirstPriorityError(errorItems);

  if (!errorToDisplay) return null;

  const { title, message, onRetryCb } = errorToDisplay;
  return (
    <AlertBanner
      severity="error"
      sx={{
        color: "black",
        maxWidth: 300,
        margin: "10px auto",
        fontSize: "0.875rem",
        padding: "0px 10px"
      }}
      skipCustomTypography
    >
      <Typography variant="subtitle1" fontWeight={500}>
        {title}
      </Typography>
      <Typography variant="body2">{message}</Typography>
      {defaultRetryFn && retryActionButtonText && (
        <Button
          color="info"
          onClick={onRetryCb || defaultRetryFn}
          sx={{ marginTop: 1 }}
        >
          {retryActionButtonText}
        </Button>
      )}
    </AlertBanner>
  );
};

const Port = ({
  portId,
  portSideIndex,
  binToPortTestLoopingEnabled,
  portIdToDisplay,
  portReadyDispatchedRef,
  updateBinToPortLoopBtnStatus
}: PortInheritedProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const workstation = useAppSelector(selectThisWorkstation);
  const [closeBin, { error: closeBinError }] = useCloseBinMutation();
  const [getNextBin, { error: getNextBinError }] = useRequestNextBinMutation();
  const shouldCycleBins = workstation?.multiPortEnabled
    ? portSideIndex === 0 // when multiPortEnabled option is on, we only want to use the parentPort to cycle (we pick first portSideIndex)
    : true; // when multiPortEnabled option is off, every port cycles

  const {
    binPortSideIndex,
    binState,
    error: portStatusError,
    activityState,
    refetch,
    ...portStatus
  } = usePortStatusQuery(portId, portSideIndex);
  const binToPortTestSession = useAppSelector(selectBinToPortTestSession);
  const isCurrentPortEnabled =
    binToPortTestSession.portReadinessMap?.[portId] ?? false;
  const selectedBinNumber = portStatus.selectedBin;
  const isPortReady = portStatus.isReady;

  const {
    hasErrors,
    errorItems
  }: {
    hasErrors: boolean;
    errorItems: ErrorItem[];
  } = useMemo(() => {
    const errorItems: ErrorItem[] = [
      { title: "Getting port status failed", error: portStatusError },
      { title: "Closing bin failed", error: closeBinError },
      { title: "Request next bin failed", error: getNextBinError }
    ];
    const hasErrors = errorItems.some((item) => Boolean(item.error));
    return {
      hasErrors,
      errorItems
    };
  }, [closeBinError, getNextBinError, portStatusError]);
  const hasErrorsPrev = usePrevious<boolean>(hasErrors);

  const isBinToPortLoopEnabled = binToPortTestLoopingEnabled && !hasErrors;

  const cycleBin = useCallback(
    async (errorTypeRetry?: "closeBinError" | "getNextBinError") => {
      const shouldSkipBinCycling =
        !binToPortTestLoopingEnabled ||
        hasErrors ||
        !workstation ||
        !selectedBinNumber ||
        !shouldCycleBins;

      if ((shouldSkipBinCycling && !errorTypeRetry) || !workstation) return;

      //clear any existing timeout before scheduling a new one
      //this makes sure that a Retry button click never leaves the "old" timer lying around
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (
        (selectedBinNumber && !errorTypeRetry) ||
        (selectedBinNumber && errorTypeRetry === "closeBinError")
      ) {
        await runAsyncSafely(
          closeBin({
            autostoreGridId: workstation.autostoreGridId,
            portId,
            binNumber: selectedBinNumber
          }).unwrap()
        );
      }

      const getNextBinDelay =
        errorTypeRetry === "getNextBinError" || !selectedBinNumber ? 0 : 2000;

      timeoutRef.current = setTimeout(() => {
        void (async () => {
          await runAsyncSafely(
            getNextBin({
              autostoreGridId: workstation.autostoreGridId,
              portId
            }).unwrap()
          );
        })();
      }, getNextBinDelay);
    },
    [
      binToPortTestLoopingEnabled,
      hasErrors,
      workstation,
      selectedBinNumber,
      shouldCycleBins,
      closeBin,
      portId,
      getNextBin
    ]
  );
  const retryBinOrPortAction = useCallback(async () => {
    if (portStatusError) {
      await refetch();
      return;
    }
    if (closeBinError) {
      await cycleBin("closeBinError");
      return;
    }
    if (getNextBinError) {
      await cycleBin("getNextBinError");
      return;
    }
  }, [closeBinError, cycleBin, getNextBinError, portStatusError, refetch]);

  const errorMessageByType = getAlertMessageFromErrorList(
    errorItems,
    t("retry"),
    retryBinOrPortAction
  );

  useEffect(() => {
    if (
      isPortReady &&
      selectedBinNumber &&
      !isCurrentPortEnabled &&
      !portReadyDispatchedRef.current &&
      !hasErrors
    ) {
      //In the scenario where multi port is enabled for the ws
      //this hook runs once per port instance
      //using the ref here to remember that we’ve already notified redux store
      //about this port’s readiness so that re-renders don’t cause a second dispatch
      //eslint-disable-next-line no-param-reassign
      portReadyDispatchedRef.current = true;

      //This updates portReadinessMap[portId] = true
      //which the parent component uses to enable the `bin to port loop` button
      dispatch(
        updateBinToPortLoopStatus({
          portId,
          portReadinessValue: true
        })
      );
    }
  }, [
    dispatch,
    portId,
    portReadyDispatchedRef,
    isPortReady,
    selectedBinNumber,
    hasErrors,
    isCurrentPortEnabled
  ]);

  useEffect(() => {
    if (portStatusError && portSideIndex === 0) {
      dispatch(
        addEvent({
          text: `Bin to Port: failed getting port status for port ${portId}`,
          state: "Failed",
          testOrigin: "Bin to Port"
        })
      );
      //make sure to update bin to port test status
      //skip logging since we inidividually log the error
      dispatch(
        setSingleStepTestStatus({
          testingToolName: "Bin to Port",
          eventStatus: "Failed",
          skipLogging: true
        })
      );
    }
  }, [dispatch, portStatusError, portId, portSideIndex]);

  useEffect(() => {
    if (hasErrors && !hasErrorsPrev) {
      //clear any existing timeout on error
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      //If there are errors now and there were no errors before
      //we need to disable the bin to port loop button
      updateBinToPortLoopBtnStatus(false);
      //and update port readiness map
      dispatch(
        updateBinToPortLoopStatus({
          portId,
          portReadinessValue: false
        })
      );
    } else if (!hasErrors && hasErrorsPrev) {
      //If there were errors before and now there are no errors
      //we need to re-enable the bin to port loop button by updating port readiness map
      //which the parent component uses to enable the `bin to port loop` button
      dispatch(
        updateBinToPortLoopStatus({
          portId,
          portReadinessValue: true
        })
      );
    }
  }, [
    dispatch,
    hasErrors,
    hasErrorsPrev,
    portId,
    updateBinToPortLoopBtnStatus
  ]);

  //Cycling bins runs every 3 seconds
  usePromiseInterval(cycleBin, 3000, isBinToPortLoopEnabled);

  return (
    <Stack
      sx={{
        flexDirection: workstation?.multiPortEnabled ? "row" : "column",
        gap: 3,
        alignItems: "center",
        width: "100%",
        mb: workstation?.multiPortEnabled ? 2 : 0,
        "@media (max-width: 900px)": {
          gap: 1
        }
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateRows: "auto 1fr",
          gap: 1,
          maxWidth: 350,
          minWidth: 250
        }}
      >
        <Typography variant="h5" fontWeight={500} textAlign="center">
          Port {portIdToDisplay}
        </Typography>

        <AutostoreBin
          state={activityState}
          binId={selectedBinNumber}
          pickQuantity={""}
          pickCompartment={null}
          numberOfRows={
            (portSideIndex === binPortSideIndex &&
              binState?.binConfiguration?.horizontalCompartmentCount) ||
            1
          }
          numberOfColumns={
            (portSideIndex === binPortSideIndex &&
              binState?.binConfiguration?.verticalCompartmentCount) ||
            1
          }
        />
      </Box>
      {!!errorMessageByType && portSideIndex === 0 && errorMessageByType}
    </Stack>
  );
};

export default Port;
